import { RotateCw, Unplug } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { describeError } from '@/api/errors';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { useSessionContext } from './session-context';

export function BootScreen() {
  return (
    <div className="flex h-dvh items-center justify-center bg-canvas">
      <div className="flex items-center gap-2 text-base text-fg-subtle">
        <Spinner /> Restoring your session…
      </div>
    </div>
  );
}

/** Gate for the console: restores the tab's session, or sends the visitor to /login?next=… */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state, retry } = useSessionContext();
  const location = useLocation();

  if (state.status === 'loading') return <BootScreen />;

  if (state.status === 'error') {
    const described = describeError(state.error);
    return (
      <div className="flex h-dvh items-center justify-center bg-canvas p-6">
        <EmptyState
          icon={Unplug}
          title="Can’t load your session"
          description={described.detail}
          action={
            <Button onClick={retry} icon={<RotateCw />}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  if (state.status === 'anonymous') {
    const next = `${location.pathname}${location.search}`;
    const params = new URLSearchParams();
    if (next !== '/') params.set('next', next);
    if (state.reason === 'expired') params.set('expired', '1');
    const query = params.toString();
    return <Navigate to={`/login${query ? `?${query}` : ''}`} replace />;
  }

  return <>{children}</>;
}
